import Link from 'next/link';
import classNames from 'classnames';

import { StatLabel } from './panel';
import PlayerImage from '@components/player/image';
import PlayerSearch from '@components/player-search';
import { PlayerNewsNote } from '@components/player/PlayerNewsNote';
import type { Player as FfhPlayer } from '@api-service/players';
import {
  describeAvailability,
  formatNumber,
  formatOwnership,
  formatPrice,
  NO_VALUE
} from '@lib/page-utils/player-profile/format';
import {
  type AvailabilitySeverity,
  getAvailabilitySeverity,
  type PlayerStatus
} from '@lib/my-team/player-availability';

const PLAYER_IMAGE_SIZE = 116;

// Availability pill per severity, shared with the pick page: green when fit,
// amber for a mild doubt, red for anything worse.
const AVAILABILITY_PILL_STYLES: Record<AvailabilitySeverity, string> = {
  none: 'bg-success-50 text-success-700',
  caution: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700'
};

export type Breadcrumb = { label: string; href?: string };

type HeroKpi = {
  label: string;
  value: string;
};

type PlayerProfileHeroProps = {
  displayName: string;
  fullName: string | null;
  position: string;
  teamName: string;
  price: number | null;
  status: string | null;
  chanceOfPlaying: number | null;
  /** FPL's availability note, shown beneath the status pill when present. */
  news?: string | null;
  newsAddedAt?: string | null;
  imageUrl: string | null;
  kitImageUrl: string | null;
  seasonPoints: number | null;
  /** Points per appearance, computed from the stats endpoint. */
  pointsPerGame: number | null;
  ownership: number | null;
  minutesPerStart: number | null;
  comparisonHref: string;
  optaHref: string;
  /**
   * Overrides the trail when the profile is reached through the slug routes,
   * where the club crumb links to that club's index. The default is the
   * legacy query-param route's trail.
   */
  breadcrumbs?: Breadcrumb[];
  /** Jumps straight to another player's profile from the hero search. */
  onPlayerSearchClick: (player: FfhPlayer) => void;
  onPaywallOpen: () => void;
};

const OutlinePill = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-[13px] font-semibold text-neutral-700">
    {children}
  </span>
);

export const PlayerProfileHero = ({
  displayName,
  fullName,
  position,
  teamName,
  price,
  status,
  chanceOfPlaying,
  news,
  newsAddedAt,
  imageUrl,
  kitImageUrl,
  seasonPoints,
  pointsPerGame,
  ownership,
  minutesPerStart,
  comparisonHref,
  optaHref,
  breadcrumbs,
  onPlayerSearchClick,
  onPaywallOpen
}: PlayerProfileHeroProps) => {
  const availability = describeAvailability(status, chanceOfPlaying);
  const availabilitySeverity = getAvailabilitySeverity(
    (status ?? undefined) as PlayerStatus | undefined,
    chanceOfPlaying
  );

  const trail: Breadcrumb[] = breadcrumbs ?? [
    { label: 'Toolbox', href: '/tools' },
    { label: 'Player profiles', href: '/player-profile' },
    { label: teamName },
    { label: displayName }
  ];

  const kpis: HeroKpi[] = [
    {
      label: 'Season points',
      value: seasonPoints === null ? NO_VALUE : String(seasonPoints)
    },
    { label: 'Points / game', value: formatNumber(pointsPerGame, 2) },
    { label: 'Ownership', value: formatOwnership(ownership) },
    {
      label: 'Minutes / start',
      value:
        minutesPerStart === null
          ? NO_VALUE
          : String(Math.round(minutesPerStart))
    }
  ];

  return (
    <div className="border-b border-neutral-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 pt-4 pb-6 sm:px-6 lg:px-12">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
              {trail.map((crumb, index) => (
                <li key={crumb.label} className="flex items-center gap-2">
                  {index > 0 && (
                    <span aria-hidden className="text-neutral-300">
                      /
                    </span>
                  )}
                  {crumb.href ? (
                    <Link
                      href={crumb.href}
                      className="hover:text-neutral-800 hover:underline"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      className={classNames(
                        index === trail.length - 1 && 'text-neutral-800'
                      )}
                    >
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <div className="w-full sm:w-72">
            <PlayerSearch
              searchId="player-profile-hero-search"
              label="Search for another player"
              onPlayerClick={onPlayerSearchClick}
              hasPaywall={false}
              showPaywall={onPaywallOpen}
            />
          </div>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-6">
            {/* Fixed square, because as a stretched flex-column child the
                rounded-full crop turns into a full-width lozenge on mobile. */}
            <div className="h-[116px] w-[116px] shrink-0 overflow-hidden rounded-full border border-neutral-200 bg-neutral-100">
              <PlayerImage
                src={imageUrl}
                kitImageUrl={kitImageUrl ?? undefined}
                height={PLAYER_IMAGE_SIZE}
                width={PLAYER_IMAGE_SIZE}
              />
            </div>

            <div className="min-w-0">
              <h1 className="font-sans text-4xl font-bold leading-[0.94] tracking-[-0.03em] text-neutral-900 sm:text-5xl lg:text-[54px]">
                {displayName}
              </h1>
              {fullName && fullName !== displayName && (
                <p className="mt-2 text-sm text-neutral-500">{fullName}</p>
              )}

              <div className="mt-3.5 flex flex-wrap items-center gap-2">
                <OutlinePill>{position}</OutlinePill>
                <OutlinePill>{teamName}</OutlinePill>
                <span className="inline-flex items-center rounded-md bg-primary-600 px-2.5 py-1 text-[13px] font-bold text-white">
                  {formatPrice(price)}
                </span>
                <span
                  className={classNames(
                    'inline-flex items-center rounded-md px-2.5 py-1 text-[13px] font-semibold',
                    AVAILABILITY_PILL_STYLES[availabilitySeverity]
                  )}
                  data-cy-availability={availabilitySeverity}
                >
                  {availability.label}
                </span>
              </div>
              <PlayerNewsNote
                news={news}
                newsAddedAt={newsAddedAt}
                className="mt-3"
              />

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <Link
                  href={comparisonHref}
                  className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  Compare with…
                </Link>
                <Link
                  href={optaHref}
                  className="inline-flex min-h-[44px] items-center rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-800 hover:bg-neutral-100"
                >
                  Full OPTA stats
                </Link>
              </div>
            </div>
          </div>

          <div className="grid w-full shrink-0 grid-cols-2 gap-px overflow-hidden rounded-xl border border-neutral-200 bg-neutral-200 lg:w-[340px]">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="bg-white px-4 py-3.5">
                <StatLabel>{kpi.label}</StatLabel>
                <p className="mt-1 font-sans text-[26px] font-bold tabular-nums leading-none text-neutral-900">
                  {kpi.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
